import React, { useState } from "react";
import { FormattedMessage } from "react-intl";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import moment from "moment";
import _ from "lodash";
import { Modal, Table, Typography, Button, Menu, Dropdown } from "antd";
import NewColumn from "./NewColumn";
import EditColumn from "./EditColumn";
import { arrayMoveImmutable } from "array-move";
import { MenuOutlined } from "@ant-design/icons";
import {
  createTableItem,
  updateTableItem,
  deleteTableItem,
  changeTableOrder,
} from "../../features/settings/settingsSlice";
import { getMetaSystemTable } from "../../api/server";
import { useQuery } from "react-query";
import FormHeaderWrapper from "../forms/FormHeader.style";
import {
  EditOutlined,
  DeleteOutlined,
  DownOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import RestrictedComponent from "../../components/util/RestrictedComponent";
import {
  SortableContainer,
  SortableElement,
  SortableHandle,
} from "react-sortable-hoc";
const DragHandle = SortableHandle(() => (
  <MenuOutlined
    style={{
      cursor: "grab",
      color: "#999",
    }}
  />
));
const { Title, Text } = Typography;
const SortableItem = SortableElement((props) => <tr {...props} />);
const SortableBody = SortableContainer((props) => <tbody {...props} />);

const ColumnsSettingsList = (props) => {
  const { name, comp, compName, tableComp, type } = props;
  const dispatch = useDispatch();
  const [modal, contextHolder] = Modal.useModal();
  const [visible, setVisible] = useState(false);
  const [time, setTime] = useState(moment().format());
  const [editedRecord, setEditedRecord] = useState(null);
  const [dataSource, setDataSource] = useState([]);
  const intl = useIntl();

  const { isLoading, data } = useQuery(
    [{ name, time }],
    async () => {
      const result = await getMetaSystemTable(name);
      const sorted = result.data.items
        ? _.orderBy(
            result.data.items.map((i) => ({ ...i, index: i.order })),
            ["order"],
            ["asc"]
          )
        : [];
      setDataSource(sorted);
      return result.data.items;
    },
    {
      keepPreviousData: true,
      staleTime: 0, //no cached time
      enabled: name !== null && name !== undefined,
    }
  );

  const onAddClick = () => {
    setVisible(true);
  };

  const onAddColumn = async (values) => {
    const action = await dispatch(
      createTableItem({
        record: { ...values, order: dataSource.length + 1 },
        source: name,
      })
    );
    if (!action.error) {
      hide();
      setTime(moment().format());
    }
  };

  const onEdit = (record) => {
    setEditedRecord(record);
    setVisible(true);
  };

  const onEditColumn = async (record) => {
    const action = await dispatch(updateTableItem({ record, source: name }));
    if (!action.error) {
      setVisible(false);
      setEditedRecord(null);
      setTime(moment().format());
    }
  };

  const onDeleteColumn = (record) => {
    const title = intl.formatMessage({
      id: "app.columnsSettings.deleteColumnTitle",
    });
    const content = intl.formatMessage({
      id: "app.columnsSettings.deleteColumnContent",
    });
    modal.confirm({
      title: title,
      icon: <ExclamationCircleOutlined />,
      content: content,
      onOk() {
        return onConfirm(record);
      },
      onCancel() {},
    });
  };

  const onConfirm = async (record) => {
    const action = await dispatch(deleteTableItem({ source: name, record }));
    if (!action.error) {
      const sorted = dataSource
        ? _.orderBy(
            dataSource.filter((k) => k.id !== record.id),
            ["order"],
            ["asc"]
          ).map((i, index) => ({ id: i.id, order: index + 1 }))
        : [];
      onChangeOrder({
        items: sorted,
      });
    }
  };

  const onChangeOrder = async (record) => {
    const action = await dispatch(
      changeTableOrder({ source: `${name}/order`, record })
    );
    if (!action.error) {
      setTime(moment().format());
    }
  };

  const handleAdminMenuClick = (e, record) => {
    switch (e.key) {
      case "1":
        onEdit(record);
        break;
      case "2":
        onDeleteColumn(record);
        break;

      default:
        break;
    }
  };

  const hide = () => {
    setVisible(false);
    setEditedRecord(null);
  };

  const onSortEnd = ({ oldIndex, newIndex }) => {
    if (oldIndex !== newIndex) {
      const newData = arrayMoveImmutable(
        dataSource.slice(),
        oldIndex,
        newIndex
      ).filter((el) => !!el);
      const newIndexed = newData.map((item, index) => ({
        ...item,
        index: index + 1,
      }));
      setDataSource(newIndexed);
      onChangeOrder({
        items: newIndexed.map((i) => ({ id: i.id, order: i.index })),
      });
    }
  };

  const DraggableContainer = (props) => (
    <SortableBody
      useDragHandle
      disableAutoscroll
      helperClass="row-dragging"
      onSortEnd={onSortEnd}
      {...props}
    />
  );

  const DraggableBodyRow = ({ className, style, ...restProps }) => {
    // function findIndex base on Table rowKey props and should always be a right array index
    const index = dataSource.findIndex(
      (x) => x.index === restProps["data-row-key"]
    );
    return <SortableItem index={index} {...restProps} />;
  };

  return (
    <div>
      {contextHolder}
      <FormHeaderWrapper>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Title level={5} style={{ margin: 0 }}>
            <FormattedMessage id={`app.columnsSettings.${type}`} />
          </Title>
          <RestrictedComponent
            resource={"organization.settings"}
            action={"editor"}
            comp={null}
          >
            <Button type="primary" onClick={onAddClick}>
              <FormattedMessage id={`app.actions.addColumnsSettings`} />
            </Button>
          </RestrictedComponent>
        </div>
      </FormHeaderWrapper>

      <Table
        bordered
        scroll={{ y: window.innerHeight - 526 }}
        dataSource={dataSource}
        pagination={false}
        rowKey="order"
        components={{
          body: {
            wrapper: DraggableContainer,
            row: DraggableBodyRow,
          },
        }}
        columns={[
          {
            title: "",
            dataIndex: "order",
            width: 30,
            className: "drag-visible",
            render: () => <DragHandle />,
          },
          {
            title: intl.formatMessage({
              id: "app.columnsSettings.order",
            }),
            dataIndex: "order",
            render(text, record) {
              return <Text>{text}</Text>;
            },
          },
          tableComp,
          {
            title: intl.formatMessage({
              id: "app.columnsSettings.display_name",
            }),
            dataIndex: "display_name",
            render(text, record) {
              return <Text>{text}</Text>;
            },
          },
          {
            title: intl.formatMessage({
              id: "app.columnsSettings.actions",
            }),
            width: "130px",
            render(text, record) {
              const items = [
                {
                  label: intl.formatMessage({
                    id: "app.columnsSettings.edit",
                  }),
                  key: "1",
                  icon: <EditOutlined />,
                },
                {
                  label: intl.formatMessage({
                    id: "app.columnsSettings.delete",
                  }),
                  key: "2",
                  icon: <DeleteOutlined />,
                  style: { color: "red" },
                },
              ];
              const menu = {
                items,
                onClick: (e) => handleAdminMenuClick(e, record),
              };

              return (
                <RestrictedComponent
                  resource={"organization.settings"}
                  action={"editor"}
                  comp={null}
                >
                  <div style={{ display: "flex" }}>
                    <Dropdown trigger={["click"]} menu={menu}>
                      <Button type="link">
                        {intl.formatMessage({
                          id: "app.columnsSettings.options",
                        })}
                        <DownOutlined />
                      </Button>
                    </Dropdown>
                  </div>
                </RestrictedComponent>
              );
            },
          },
        ]}
        loading={isLoading}
      />
      <Modal
        closable={false}
        maskClosable={false}
        open={visible}
        footer={null}
        onCancel={hide}
        title={null}
        width={"fit-content"}
        styles={{
          body: {
            padding: 0,
          },
        }}
        destroyOnClose={true}
      >
        {editedRecord ? (
          <EditColumn
            hide={hide}
            addText={<FormattedMessage id="app.columnsSettings.editColumn" />}
            activeColumn={editedRecord}
            comp={comp}
            onEditColumn={onEditColumn}
            compName={compName}
          />
        ) : (
          <NewColumn
            onAddColumn={onAddColumn}
            hide={hide}
            addText={<FormattedMessage id="app.columnsSettings.addNewColumn" />}
            comp={comp}
            compName={compName}
          />
        )}
      </Modal>
    </div>
  );
};
export default ColumnsSettingsList;
